import { useState } from 'react';
import type { Bulle as BulleData, Etat, Inventaire as InventaireData } from '../partage/api/types';
import { aujourdhui } from '../partage/datesEnMots';
import type { EtatAnnulation } from '../inventaire';
import { EchecApi } from '../partage/api/client';
import { useAnnulerValidation, useDeplacer, useModifier, useValider } from '../partage/useArbre';
import { oublierLeGeste, retenirLeGeste, souvenirDe } from './souvenirDesGestes';

/**
 * Un geste sur une bulle, et **ce qu'il laisse derrière**.
 *
 * C'est la seule raison d'être de ce fichier : dans cette app, cocher une case ne coche pas
 * une case. Ça peut vider une bulle de ce qu'elle contenait encore (l'inventaire). Ce que le
 * geste déplace ailleurs (la cascade des dates) ne s'annonce plus dans un bandeau (chantier
 * `flottaison-suite`, ticket 09) : il se voit sur la carte et dans les dates.
 *
 * Les tenir ici, et pas dans l'écran, garantit qu'aucun chemin ne les contourne : **valider
 * passe toujours par `valider`, jamais par un simple changement d'état** (SPEC §3). Un
 * `modifier` avec `etat: 'fait'` posé en passant quelque part rendrait l'inventaire
 * optionnel, et un inventaire optionnel est un inventaire qui ne s'affiche jamais.
 */
export function useGestesDeFeuille() {
  const modifier = useModifier();
  const valider = useValider();
  const deplacer = useDeplacer();
  const annuler = useAnnulerValidation();
  // Les coches de l'inventaire ont leurs propres mutations : elles ne doivent ni rouvrir un
  // inventaire, ni prêter leur état (« en cours », « refusée ») au « annuler » du haut.
  const cocher = useValider();
  const decocher = useAnnulerValidation();
  // Se raviser depuis la fiche (ticket 29) : ni l'inventaire ni ses coches ne s'en mêlent.
  const defaireCoche = useAnnulerValidation();
  const defaireAttente = useModifier();

  const [inventaire, setInventaire] = useState<InventaireData | null>(null);

  /** Valider, et recevoir l'inventaire de ce qui restait dedans. */
  function validerBulle(idBulle: string) {
    valider.mutate(idBulle, {
      onSuccess: (resultat) => {
        retenirLeGeste(idBulle, {
          geste: 'fait',
          etatAvant: resultat.etatAvant,
          dateActionAvant: null,
          avecInventaire: resultat.restait.length > 0,
        });
        if (resultat.restait.length > 0) setInventaire(resultat);
      },
    });
  }

  /** Modifier une bulle. */
  function modifierBulle(idBulle: string, champs: Partial<BulleData>) {
    modifier.mutate(
      { id: idBulle, champs },
      {
        onSuccess: () => {
          // Un état posé par un autre chemin : ce que la fiche retenait ne vaut plus.
          if (champs.etat !== undefined) oublierLeGeste(idBulle);
        },
      }
    );
  }

  /**
   * Se raviser, depuis l'inventaire : la bulle retrouve l'état d'avant la coche.
   *
   * L'inventaire se ferme : il parlait d'une validation qui n'existe plus.
   */
  function annulerValidation() {
    if (!inventaire) return;
    annuler.mutate(
      { id: inventaire.bulle.id, etatAvant: inventaire.etatAvant },
      {
        onSuccess: () => {
          oublierLeGeste(inventaire.bulle.id);
          setInventaire(null);
        },
      }
    );
  }

  /**
   * « j'attends un retour », en retenant ce qu'il faudra rendre si l'on se ravise. Retenu
   * AVANT d'écrire : `modifier` est optimiste, la fiche dit « En instruction » et « Annuler » dès
   * le clic, et l'annulation doit déjà savoir quoi rendre. Oublié si l'écriture échoue.
   *
   * Au téléphone, le délai choisi en même temps (3 jours, 10 jours, 3 semaines, une date
   * précise) part dans la même écriture (chantier `telephone-projets-fiche`, ticket 03).
   * « Annuler » rend l'état et le jour du geste, pas ce délai : il reste posé, comme s'il
   * avait été posé dans « Quand ».
   */
  function attendre(bulle: BulleData, avec: Partial<BulleData> = {}) {
    // Une bulle acquise se défait d'abord (« Annuler ») : l'attente ne la décoche pas.
    if (bulle.etat === 'en_attente_confirmation' || bulle.etat === 'fait') return;
    retenirLeGeste(bulle.id, {
      geste: 'attente',
      etatAvant: bulle.etat,
      dateActionAvant: bulle.dateAction,
      avecInventaire: false,
    });
    modifier.mutate(
      { id: bulle.id, champs: { ...avec, etat: 'en_attente_confirmation' } },
      { onError: () => oublierLeGeste(bulle.id) }
    );
  }

  /**
   * Se raviser depuis la fiche : reclicker « c'est fait » ou « j'attends un retour »
   * (ticket 29). La bulle retrouve l'état d'avant le geste, et rien d'autre ne bouge : ce
   * que l'inventaire a coché, gardé comme idée, supprimé ou déplacé reste (la fiche le dit
   * avant le clic).
   *
   * Sans souvenir (page rechargée, geste fait ailleurs), elle redevient à faire : la fiche
   * l'écrit sur le bouton.
   */
  function defaire(bulle: BulleData) {
    const souvenir = souvenirDe(bulle);
    const montrer = () => oublierLeGeste(bulle.id);

    if (bulle.etat === 'fait') {
      defaireCoche.mutate(
        { id: bulle.id, etatAvant: souvenir?.etatAvant ?? 'a_faire' },
        { onSuccess: montrer }
      );
      return;
    }
    if (bulle.etat !== 'en_attente_confirmation') return;

    // `attendre` refuse une bulle acquise : l'état d'avant n'est jamais « fait » ici, et
    // revenir à « acquis » sans passer par `valider` n'arrive donc pas.
    const etat = souvenir && souvenir.etatAvant !== 'fait' ? souvenir.etatAvant : 'a_faire';
    const champs: Partial<BulleData> = souvenir
      ? { etat, dateAction: souvenir.dateActionAvant }
      : { etat };
    defaireAttente.mutate({ id: bulle.id, champs }, { onSuccess: montrer });
  }

  // Seul le refus du serveur dit « elle a changé depuis ». Une coupure réseau laisse
  // « annuler » disponible : on peut réessayer, rien n'a été écrit.
  const refusee = annuler.error instanceof EchecApi && annuler.error.code === 'plus_acquise';
  const annulation: EtatAnnulation = annuler.isPending ? 'en_cours' : refusee ? 'refusee' : 'repos';

  function changerEtat(idBulle: string, etat: Etat) {
    if (etat === 'fait') validerBulle(idBulle);
    else modifierBulle(idBulle, { etat });
  }

  return {
    inventaire,
    validerBulle,
    annulerValidation,
    annulation,
    modifierBulle,
    changerEtat,
    attendre,
    defaire,
    /**
     * Un geste de la fiche est en route (le sien, ou l'annulation) : le bouton ne se
     * reclique pas, deux écritures croisées rendraient un état faux.
     */
    defaireEnCours:
      defaireCoche.isPending ||
      defaireAttente.isPending ||
      modifier.isPending ||
      valider.isPending,
    /**
     * Cocher une bulle restée, depuis l'inventaire (ticket 28). C'est bien `valider`, pas un
     * simple changement d'état : même chemin que partout. Mais ce qu'elle contient encore ne
     * rouvre pas d'inventaire par-dessus celui-ci : on coche vite, une ligne après l'autre. Rend l'état d'avant, que le décochage remettra.
     */
    cocherRestee: async (idBulle: string): Promise<Etat> => {
      const { etatAvant } = await cocher.mutateAsync(idBulle);
      // Sa fiche saura aussi l'annuler. Rien n'a été rangé dedans : pas d'inventaire.
      retenirLeGeste(idBulle, {
        geste: 'fait',
        etatAvant,
        dateActionAvant: null,
        avecInventaire: false,
      });
      return etatAvant;
    },
    /** Décocher : la bulle restée retrouve l'état d'avant sa coche, et rien d'autre. */
    decocherRestee: async (idBulle: string, etatAvant: Etat): Promise<void> => {
      await decocher.mutateAsync({ id: idBulle, etatAvant });
      oublierLeGeste(idBulle);
    },
    /**
     * Garder comme idée (« En réflexion », ticket 18 de retours-testeurs) : elle sort du
     * décompte, ne retient rien, et redevient une tâche dès qu'on lui donne un critère.
     */
    garderCommeIdee: (idBulle: string) =>
      modifier.mutate(
        { id: idBulle, champs: { etat: 'sans_objet' } },
        { onSuccess: () => oublierLeGeste(idBulle) }
      ),
    /**
     * Déplacer une bulle restée, depuis l'inventaire. La promesse échoue si le serveur n'a
     * rien écrit (une boucle, un autre refus, le réseau) : la ligne revient alors comme avant
     * le geste, et l'inventaire dit pourquoi (chantier `deplacer`, ticket 03).
     */
    deplacerAilleurs: async (idBulle: string, parentId: string | null): Promise<void> => {
      await deplacer.mutateAsync({ id: idBulle, parentId });
    },
    /** Relancer : l'attente du monde repart d'aujourd'hui. Jamais un compteur de retard. */
    relancer: (idBulle: string) =>
      modifier.mutate({ id: idBulle, champs: { dateAction: aujourdhui() } }),
    fermerInventaire: () => {
      setInventaire(null);
      annuler.reset();
    },
  };
}
